import { RouterProvider, createRouter } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { onSessionExpired } from "./api/sessionExpiry";
import { useAuth } from "./contexts/AuthContext";
import { queryClient } from "./query";
import { NotFoundComponent } from "./routes/__root";
import { routeTree } from "./routeTree.gen";

// The router instance and its provider component are one unit: main.tsx hands
// this same instance to Sentry's tracing integration, and a second instance
// would mean a second history.
// eslint-disable-next-line react-refresh/only-export-components
export const router = createRouter({
  routeTree,
  context: { queryClient, auth: undefined! },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  // Without this, a notFound() thrown from a route's beforeLoad renders an
  // empty document — no 404, no error, just a white page.
  defaultNotFoundComponent: NotFoundComponent,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Auth + Router wrapper that:
 *  - keeps router context synced with auth
 *  - waits for auth + current route loaders (+ any bootstrap queries)
 *  - then calls onReady()
 *
 * Lives here rather than in main.tsx so the boot gate below is testable:
 * importing main.tsx would run Sentry.init, uploadManager.init and
 * root.render.
 */
export function AuthRouterProvider({ onReady }: { onReady?: () => void }) {
  const auth = useAuth();

  // keep router context up to date with auth
  router.update({
    context: { ...router.options.context, auth },
  });

  useSessionExpiredRedirect(auth);

  useEffect(() => {
    // Hold everything until the session probe settles. onReady() resolves a
    // one-shot promise, and once boot.finishWhen(appReady) is satisfied the
    // overlay is gone for good — it cannot be put back for the probe.
    if (auth.isBootstrapping) return;

    let cancelled = false;

    void (async () => {
      // Ensure the router has loaded the *current* location's matches.
      try {
        await router.load();
      } catch (error: unknown) {
        console.error("router.load() failed: ", error);
      }

      if (!cancelled) onReady?.();
    })();

    return () => {
      cancelled = true;
    };
  }, [onReady, auth.isBootstrapping]);

  // Render gate. Mounting the router now would let _auth's beforeLoad throw its
  // /login redirect and paint the login form underneath an overlay that has not
  // become visible yet — the exact flash the probe exists to avoid. Ordering is
  // safe by construction: router.update() above runs in the render body, so the
  // render that flips isBootstrapping false has already published fresh auth
  // context before this effect runs.
  if (auth.isBootstrapping) return null;

  return <RouterProvider router={router} />;
}

/**
 * A failed token refresh reaches the app as a rejected API call, not as a
 * route error — inline queries swallow it and never reach an errorComponent.
 * connectClient reports it instead; this turns that into logout + redirect.
 */
function useSessionExpiredRedirect({
  isAuthenticated,
  logout,
}: ReturnType<typeof useAuth>) {
  const [expired, setExpired] = useState(false);

  useEffect(
    () =>
      onSessionExpired(() => {
        setExpired(true);
        enqueueSnackbar("Your session has expired. Please log in again.", {
          variant: "error",
          key: "session-expired",
        });
        void logout();
      }),
    [logout],
  );

  // Wait for logged-out auth to reach router context, or /login bounces back.
  useEffect(() => {
    if (isAuthenticated || !expired) return;
    setExpired(false);
    void router.navigate({
      to: "/login",
      search: { redirect: router.state.location.href },
    });
  }, [isAuthenticated, expired]);
}
